import React from 'react'
import { Building2, ListChecks } from 'lucide-react'
import { cn } from '../UI/cn'
import { Tooltip } from '../Tooltip/tooltip'
import type { ActionSummaryStructuredData } from '../../services/recommendations_api'
import type { EnrichedAction } from '../../services/recommendations_contracts'
import {
  URGENCY_ICONS,
  URGENCY_COLORS,
  URGENCY_TOOLTIPS,
  SIGNAL_TYPE_ICONS,
} from './constants'

type ViewMode = 'priority' | 'signal' | 'company'

interface SummaryStatsHeaderProps {
  data: ActionSummaryStructuredData
  className?: string
  visibleActions?: EnrichedAction[]
  viewMode?: ViewMode
  /** Number of companies with active actions (used in company view). */
  companiesCount?: number
}

interface StatCardProps {
  icon: React.ReactNode
  value: number | string
  label: string
  tooltipTitle: string
  tooltipContent: string
}

const STAT_CARD_STYLE = 'bg-surface border-line text-fg-tertiary'

/**
 * Displays a single stat card with icon, value, label, and tooltip.
 *
 * @param icon - React node to display as the icon (apply color via className on the icon itself)
 * @param value - Numeric or string value to display
 * @param label - Label text below the value
 * @param tooltipTitle - Title for the tooltip
 * @param tooltipContent - Content text for the tooltip
 */
const StatCard: React.FC<StatCardProps> = ({
  icon,
  value,
  label,
  tooltipTitle,
  tooltipContent,
}) => (
  <Tooltip
    title={tooltipTitle}
    content={tooltipContent}
    placement="top"
    delay={[300, 0]}
  >
    <div
      className={cn(
        'flex items-center gap-2 px-3 py-2 rounded-lg border cursor-help',
        STAT_CARD_STYLE
      )}
    >
      <div className="flex-shrink-0 text-fg-secondary">{icon}</div>
      <div>
        <div className="text-lg font-bold leading-tight">{value}</div>
        <div className="text-xs opacity-80 leading-tight">{label}</div>
      </div>
    </div>
  </Tooltip>
)

/**
 * Summary stats header with three stat cards whose content adapts to the active view mode.
 *
 * - Priority view: Do Now / Today / This Week counts (urgency bands)
 * - Signal type view: Unreplied Emails / Inactive Deals / Stage Changes counts
 * - Company view: Companies / Active Actions / Resolved counts
 */
export const SummaryStatsHeader: React.FC<SummaryStatsHeaderProps> = ({
  data,
  className,
  visibleActions,
  viewMode = 'priority',
  companiesCount = 0,
}) => {
  const actions = visibleActions ?? data.enriched_actions ?? []

  // Urgency band counts
  const doNowCount = actions.filter((a) => a.urgency === 'do_now').length
  const todayCount = actions.filter((a) => a.urgency === 'today').length
  const thisWeekCount = actions.filter((a) => a.urgency === 'this_week').length

  // Signal type counts
  const unrepliedCount = actions.filter(
    (a) => a.signal_type === 'unreplied_email'
  ).length
  const inactiveCount = actions.filter(
    (a) => a.signal_type === 'inactive_deal'
  ).length
  const stageChangeCount = actions.filter(
    (a) => a.signal_type === 'email_stage_change'
  ).length

  const totalActive = actions.length

  const DoNowIcon = URGENCY_ICONS.do_now
  const TodayIcon = URGENCY_ICONS.today
  const ThisWeekIcon = URGENCY_ICONS.this_week
  const MailIcon = SIGNAL_TYPE_ICONS.unreplied_email
  const InactiveIcon = SIGNAL_TYPE_ICONS.inactive_deal
  const StageIcon = SIGNAL_TYPE_ICONS.email_stage_change

  const cards: StatCardProps[] =
    viewMode === 'priority'
      ? [
          {
            icon: <DoNowIcon size={20} className={URGENCY_COLORS.do_now} />,
            value: doNowCount,
            label: 'Hot',
            tooltipTitle: URGENCY_TOOLTIPS.do_now.title,
            tooltipContent: URGENCY_TOOLTIPS.do_now.content,
          },
          {
            icon: <TodayIcon size={20} className={URGENCY_COLORS.today} />,
            value: todayCount,
            label: 'Today',
            tooltipTitle: URGENCY_TOOLTIPS.today.title,
            tooltipContent: URGENCY_TOOLTIPS.today.content,
          },
          {
            icon: (
              <ThisWeekIcon size={20} className={URGENCY_COLORS.this_week} />
            ),
            value: thisWeekCount,
            label: 'This Week',
            tooltipTitle: URGENCY_TOOLTIPS.this_week.title,
            tooltipContent: URGENCY_TOOLTIPS.this_week.content,
          },
        ]
      : viewMode === 'signal'
        ? [
            {
              icon: <MailIcon size={20} className="text-fg-secondary" />,
              value: unrepliedCount,
              label: 'Unreplied',
              tooltipTitle: 'Unreplied Emails',
              tooltipContent:
                'Inbound emails from 3-60 days ago that need a response. Automated emails are filtered out.',
            },
            {
              icon: <InactiveIcon size={20} className="text-fg-secondary" />,
              value: inactiveCount,
              label: 'Inactive Deals',
              tooltipTitle: 'Inactive Deals',
              tooltipContent:
                'Deals needing attention: 7+ days with no activity OR 14+ days stuck in the same stage.',
            },
            {
              icon: <StageIcon size={20} className="text-fg-secondary" />,
              value: stageChangeCount,
              label: 'Stage Changes',
              tooltipTitle: 'Update Deal Stage',
              tooltipContent:
                'Deals where recent inbound emails suggest a stage advancement is warranted.',
            },
          ]
        : [
            {
              icon: <Building2 size={20} className="text-fg-secondary" />,
              value: companiesCount,
              label: 'Companies',
              tooltipTitle: 'Companies',
              tooltipContent:
                'Number of companies with at least one active recommended action.',
            },
            {
              icon: <ListChecks size={20} className="text-fg-secondary" />,
              value: totalActive,
              label: 'Total',
              tooltipTitle: 'Total Actions',
              tooltipContent: `${totalActive} active action${totalActive === 1 ? '' : 's'} across ${companiesCount} ${companiesCount === 1 ? 'company' : 'companies'}.`,
            },
          ]

  return (
    <div className={cn('space-y-2', className)}>
      <div
        className={cn(
          'grid gap-2',
          cards.length === 2 ? 'grid-cols-2' : 'grid-cols-3'
        )}
      >
        {cards.map((card) => (
          <StatCard key={card.label} {...card} />
        ))}
      </div>
    </div>
  )
}
